import { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { ScrollArea } from "./ui/scroll-area";
import { Badge } from "./ui/badge";
import { cn } from "../lib/utils";
import {
  Sparkles, X, Send, Loader2, Bot, User, ChevronDown
} from "lucide-react";

const PAGE_CONTEXT = {
  "/zona-franca/oea":        "zona_franca_oea",
  "/zona-franca/cnzfe":      "zona_franca_cnzfe",
  "/zona-franca/iso22000":   "zona_franca_iso22000",
  "/zona-franca/bpm":        "zona_franca_bpm",
  "/zona-franca/haccp":      "zona_franca_haccp",
  "/zona-franca/nordom477":  "zona_franca_nordom477",
  "/zona-franca":            "zona_franca",
  "/risks": "risk",
  "/audits": "audit",
  "/documents": "document",
  "/incidents": "incident",
  "/evidence": "evidence",
  "/controls": "control",
  "/soa": "control",
  "/gap-analysis": "gap",
  "/corrective-actions": "corrective_action",
  "/reports": "report",
};

function inferContext(pathname) {
  // Match longest prefix first so /zona-franca/oea wins over /zona-franca
  const sorted = Object.entries(PAGE_CONTEXT).sort((a, b) => b[0].length - a[0].length);
  for (const [path, ctx] of sorted) {
    if (pathname.startsWith(path)) return ctx;
  }
  return "general";
}

const ZF_PROMPTS = {
  zona_franca:           ["¿Qué módulos de Zona Franca debo priorizar?", "Resúmeme las obligaciones del CNZFE", "¿Qué se necesita para certificar OEA?"],
  zona_franca_oea:       ["¿Qué evidencia colectar para el pilar de seguridad física?", "Explica los 5 pilares OEA", "Plantilla de procedimiento de inspección de contenedores"],
  zona_franca_cnzfe:     ["¿Qué reportes trimestrales exige el CNZFE?", "Explica el límite del 20% de ventas locales", "Requisitos del libro de operaciones"],
  zona_franca_iso22000:  ["¿Cómo defino el alcance del SGIA?", "Plantilla de política de inocuidad", "Qué auditar en cláusula 8 de ISO 22000"],
  zona_franca_bpm:       ["Diferencias clave entre BPM Alimentos y Farmacéuticos", "Frecuencia de control de plagas y registros requeridos", "POE para higiene del personal"],
  zona_franca_haccp:     ["¿Cómo identifico un PCC con el árbol de decisión?", "Sugiere límites críticos para cocción", "Plantilla de plan HACCP por etapa"],
  zona_franca_nordom477: ["¿Qué incluye una etiqueta NORDOM 477 conforme?", "Plan de muestreo estadístico básico", "Cómo solicitar certificación INDOCAL"],
};

const STARTER_PROMPTS = [
  "What are the top risks I should address?",
  "Help me draft an incident response procedure.",
  "What evidence do I need for ISO 27001?",
  "Explain the difference between likelihood and impact.",
];

function Message({ msg }) {
  const isUser = msg.role === "user";
  return (
    <div className={cn("flex gap-2 text-sm", isUser ? "flex-row-reverse" : "flex-row")}>
      <div className={cn(
        "w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5",
        isUser ? "bg-primary text-primary-foreground" : "bg-muted"
      )}>
        {isUser ? <User className="w-3.5 h-3.5" /> : <Bot className="w-3.5 h-3.5" />}
      </div>
      <div className={cn("flex flex-col gap-1 max-w-[82%]", isUser ? "items-end" : "items-start")}>
        <div className={cn(
          "rounded-2xl px-3 py-2 leading-relaxed whitespace-pre-wrap break-words",
          isUser
            ? "bg-primary text-primary-foreground rounded-tr-sm"
            : "bg-muted text-foreground rounded-tl-sm"
        )}>
          {msg.content}
        </div>
        {msg.suggestions?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1">
            {msg.suggestions.map((s, i) => (
              <button
                key={i}
                className="text-xs px-2 py-0.5 rounded-full border border-border hover:bg-accent transition-colors"
                onClick={() => msg.onSuggestion?.(s)}
              >
                {s}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function AICopilot() {
  const { token } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [unread, setUnread] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  const context = inferContext(location.pathname);

  useEffect(() => {
    if (open) {
      setUnread(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && open) setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const send = async (text) => {
    const msg = text.trim();
    if (!msg || loading) return;
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: msg }]);
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/ai/chat`,
        { message: msg, context_type: context },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { response, suggestions } = res.data;
      const sendSuggestion = (s) => send(s);
      setMessages(prev => [
        ...prev,
        { role: "assistant", content: response, suggestions, onSuggestion: sendSuggestion }
      ]);
      if (!open) setUnread(true);
    } catch {
      setMessages(prev => [
        ...prev,
        { role: "assistant", content: "Sorry, I couldn't connect. Please try again." }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    send(input);
  };

  const isEmpty = messages.length === 0;

  return (
    <>
      {/* Floating trigger */}
      <button
        onClick={() => setOpen(v => !v)}
        aria-label="Open AI Copilot"
        aria-expanded={open}
        className={cn(
          "fixed bottom-6 right-6 z-40 flex items-center gap-2 px-3 py-2.5 rounded-full shadow-lg",
          "bg-primary text-primary-foreground hover:bg-primary/90 transition-all",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
          open && "opacity-0 pointer-events-none"
        )}
      >
        <Sparkles className="w-4 h-4" />
        <span className="text-sm font-medium">AI</span>
        {unread && (
          <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-destructive animate-pulse" />
        )}
      </button>

      {/* Panel */}
      <div
        className={cn(
          "fixed right-0 bottom-0 top-[57px] z-40 flex flex-col",
          "w-80 sm:w-96 bg-background border-l border-border shadow-2xl",
          "transition-transform duration-base ease-in-out top-14",
          open ? "translate-x-0" : "translate-x-full"
        )}
        aria-label="AI Copilot"
        role="complementary"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center">
              <Sparkles className="w-3.5 h-3.5 text-primary" />
            </div>
            <div>
              <p className="text-sm font-semibold leading-none">AI Copilot</p>
              <p className="text-xs text-muted-foreground capitalize">{context} context</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {!isEmpty && (
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => setMessages([])}
                title="Clear chat"
              >
                <ChevronDown className="w-3.5 h-3.5" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => setOpen(false)}
              aria-label="Close"
            >
              <X className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>

        {/* Messages */}
        <ScrollArea className="flex-1 px-4">
          <div className="py-4 space-y-4">
            {isEmpty ? (
              <div className="space-y-4">
                <div className="text-center py-4">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                    <Sparkles className="w-6 h-6 text-primary" />
                  </div>
                  <p className="text-sm font-medium">ComplianceOS AI</p>
                  <p className="text-xs text-muted-foreground mt-1">Ask me anything about your compliance program.</p>
                </div>
                <div className="space-y-2">
                  {(ZF_PROMPTS[context] || STARTER_PROMPTS).map((p) => (
                    <button
                      key={p}
                      onClick={() => send(p)}
                      className="w-full text-left text-xs px-3 py-2 rounded-lg border border-border hover:bg-accent transition-colors"
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              messages.map((msg, i) => <Message key={i} msg={msg} />)
            )}
            {loading && (
              <div className="flex gap-2">
                <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center shrink-0">
                  <Bot className="w-3.5 h-3.5" />
                </div>
                <div className="bg-muted rounded-2xl rounded-tl-sm px-3 py-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>
        </ScrollArea>

        {/* Input */}
        <form
          onSubmit={handleSubmit}
          className="px-4 py-3 border-t shrink-0 flex items-end gap-2"
        >
          <Input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask about controls, risks, evidence…"
            className="flex-1 text-sm resize-none"
            disabled={loading}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send(input);
              }
            }}
          />
          <Button
            type="submit"
            size="icon"
            disabled={!input.trim() || loading}
            className="shrink-0"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </Button>
        </form>
      </div>

      {/* Backdrop (mobile only) */}
      {open && (
        <div
          className="fixed inset-0 z-30 bg-black/20 sm:hidden"
          onClick={() => setOpen(false)}
          aria-hidden="true"
        />
      )}
    </>
  );
}
